import styles from './index.module.less';
import { Form, Input, Button, message, Typography, Checkbox } from 'antd';
import { Service, OpenAPI } from '@/apis';
import type { LoginDto } from '@/apis';
import { useNavigate } from 'react-router-dom';
import type { FormProps } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useEffect } from 'react';

interface LoginFormValues {
  userName: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // 从本地存储加载记住的用户信息
  useEffect(() => {
    const savedUserInfo = localStorage.getItem('rememberedUserInfo');
    if (savedUserInfo) {
      try {
        const { userName, password, remember } = JSON.parse(savedUserInfo);
        form.setFieldsValue({
          userName,
          password,
          remember: remember || false,
        });
      } catch (error) {
        console.error('解析保存的用户信息失败:', error);
      }
    }
  }, [form]);

  const onFinish: FormProps<LoginFormValues>['onFinish'] = values => {
    console.log('Success:', values);
    const loginData: LoginDto = {
      managerName: values.userName,
      password: values.password,
    };

    Service.managerControllerLogins(loginData)
      .then((response: any) => {
        // 业务错误：errNo 非 0 表示登录失败（如 10002 账号或密码不正确）
        const errNo = response?.errNo ?? response?.data?.errNo;
        if (errNo !== undefined && errNo !== 0) {
          const errStr =
            response?.errStr ??
            response?.data?.errStr ??
            response?.errstr ??
            response?.data?.errstr ??
            '账号或密码不正确';
          message.error(errStr);
          return;
        }

        // 处理 token - 根据 Postman 响应，token 在响应根级别
        // 响应格式: { msg: "登录成功", token: "...", errNo: 0 }
        // 或者: { data: ManagerDetailResponse, errNo: 0 }
        let token = null;
        if (response?.token) {
          token = response.token;
        } else if (response?.data?.token) {
          token = response.data.token;
        } else if (typeof response === 'object' && 'token' in response) {
          token = (response as any).token;
        }

        if (token) {
          localStorage.setItem('token', token);
          OpenAPI.TOKEN = token;
        } else {
          console.warn('登录响应中未找到 token，请检查 API 响应格式');
        }

        if (values.remember) {
          localStorage.setItem(
            'rememberedUserInfo',
            JSON.stringify({
              userName: values.userName,
              password: values.password,
              remember: true,
            })
          );
        } else {
          localStorage.removeItem('rememberedUserInfo');
        }

        navigate('/');
        message.success('登录成功');
      })
      .catch((error: unknown) => {
        console.error('登录失败:', error);
        const err = error as {
          response?: {
            data?: { errStr?: string; errstr?: string; msg?: string };
            status?: number;
          };
          request?: unknown;
          message?: string;
          code?: string;
        };
        let errorMessage = '账号或密码不正确';

        const isCorsError =
          (!err?.response && err?.request) ||
          (typeof err?.message === 'string' &&
            (err.message.toLowerCase().includes('cors') ||
              err.message.toLowerCase().includes('cross-origin'))) ||
          err?.code === 'ERR_NETWORK' ||
          (err?.request && (err.request as { status?: number })?.status === 0);

        if (isCorsError) {
          errorMessage = '跨域请求失败，请检查服务器 CORS 配置或联系管理员';
        } else if (err?.response) {
          const data = err.response?.data;
          errorMessage =
            data?.errStr ??
            data?.errstr ??
            (err.response?.status === 401 ? '账号或密码不正确' : undefined) ??
            (err.response?.status === 403 ? '没有权限访问' : undefined) ??
            (err.response?.status === 404 ? '接口不存在' : undefined) ??
            (err.response?.status && err.response.status >= 500
              ? '服务器错误，请稍后重试'
              : undefined) ??
            data?.msg ??
            errorMessage;
        } else if (err?.request) {
          errorMessage = '网络请求失败，请检查网络连接';
        } else if (
          typeof err?.message === 'string' &&
          !/undefined|reading|properties/i.test(err.message)
        ) {
          errorMessage = err.message;
        }
        message.error(errorMessage);
      });
  };

  const onFinishFailed: FormProps<LoginFormValues>['onFinishFailed'] =
    errorInfo => {
      console.log('Failed:', errorInfo);
    };

  return (
    <div className={styles.loginWrap}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <img
            src='/brand/app-icon-rounded.svg'
            alt='Sinolinkway'
            className={styles.brandIcon}
          />
          <Typography.Title level={2} className={styles.title}>
            Sinolinkway Management System
          </Typography.Title>
          <Typography.Text className={styles.subtitle}>
            欢迎登录管理系统
          </Typography.Text>
        </div>

        <Form<LoginFormValues>
          form={form}
          className={styles.form}
          name='login'
          layout='vertical'
          size='large'
          initialValues={{ remember: false }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete='off'
        >
          <Form.Item
            label='用户名'
            name='userName'
            rules={[
              { required: true, message: '请输入用户名!' },
              { min: 2, message: '用户名至少2个字符!' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder='请输入用户名'
              className={styles.input}
            />
          </Form.Item>

          <Form.Item
            label='密码'
            name='password'
            rules={[
              { required: true, message: '请输入密码!' },
              { min: 6, message: '密码至少6个字符!' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder='请输入密码'
              className={styles.input}
            />
          </Form.Item>

          <Form.Item
            name='remember'
            valuePropName='checked'
            className={styles.rememberItem}
          >
            <Checkbox className={styles.rememberCheckbox}>记住密码</Checkbox>
          </Form.Item>

          <Form.Item className={styles.submitItem}>
            <Button
              type='primary'
              htmlType='submit'
              className={styles.submitBtn}
              block
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Login;
